import { useState } from 'react';
import { Pagination, Popconfirm } from 'antd';

import { useLetterhead } from '@/api/bookings';
import { useVendors } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import {
  cancelPurchaseOrder,
  closePurchaseOrder,
  purchaseOrdersKey,
  usePurchaseOrder,
  usePurchaseOrderGlance,
  usePurchaseOrders,
  type PurchaseOrder,
  type PurchaseOrderLine,
  type PurchaseOrderSummary,
} from '@/api/purchaseOrders';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import {
  DocumentHeading,
  DocumentLines,
  DocumentPaper,
  DocumentParties,
  DocumentTop,
  DocumentTotals,
  LineName,
  type DocumentColumn,
} from '@/components/DocumentSheet';
import { selectClass } from '@/components/Field';
import { hueClasses, type Hue } from '@/components/hues';
import { IconAction } from '@/components/IconAction';
import {
  Button,
  Card,
  EmptyState,
  Notice,
  PageHeading,
  SectionTitle,
} from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import type { PrintLetterhead } from '@core/api/bookings';
import { formatDocumentDate, toDayjs, toIsoDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import { billStatusLabel, billStatusTone } from '@core/purchasing/bill';
import {
  canCancel,
  canCloseShort,
  canReceive,
  orderStatusLabel,
  orderStatusTone,
} from '@core/purchasing/order';
import {
  lateWords,
  moveWords,
  orderSize,
  orderSteps,
  receivedShare,
  type OrderStep,
  type StepMove,
} from '@core/purchasing/tracker';
import { PurchaseOrderStatusValues, type PurchaseOrderStatus } from '@core/generated/enums';
import { can, canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { PrintFrame } from '../print/PrintFrame';
import { PurchaseOrderSheet } from '../print/PurchaseOrderSheet';
import { BillScreen } from './BillScreen';
import { OrderComposer } from './OrderComposer';
import { PayScreen } from './PayScreen';
import { PurchaseBillsPage } from './PurchaseBillsPage';
import { ReceiveScreen } from './ReceiveScreen';
import { VendorPaymentsPage } from './VendorPaymentsPage';

const PAGE_SIZE = 25;

const rupees = (value: number) => `₹${formatAmount(value)}`;

type Screen =
  | { kind: 'list' }
  | { kind: 'new' }
  | { kind: 'view'; id: number }
  | { kind: 'edit'; id: number }
  | { kind: 'receive'; id: number }
  | { kind: 'print'; id: number }
  | { kind: 'bill'; id: number; vendorId: number }
  | { kind: 'pay'; id: number; vendorId: number }
  | { kind: 'split'; id: number; vendorId: number }
  | { kind: 'openBill'; id: number; billId: number };

/**
 * Shop › Purchases › Purchase orders (canvas "Purchase order flow", 2026-10-25): the register with
 * the open orders in four figures, an order's own page with its four steps — ordered, received,
 * billed, paid — each with the move that takes it on, and New, Change, Receive, their bill, Pay and
 * the printed order behind it. Every insert and update is a full page; sub-screens are internal
 * state, never the URL.
 *
 * <p>
 * <strong>Nothing about what has come is worked out here.</strong> Received, pending, billed and the
 * status are the server's, read from the goods receipts and the bills; the screen only offers the
 * moves the order's state allows (`purchasing/order.ts`), so it never offers a write the server
 * would refuse.
 * </p>
 */
export function PurchaseOrdersPage({
  session,
  openId: startId,
  onExit,
}: {
  session: Session;
  /** Opens on this one — the purchase desk's history. */
  openId?: number | undefined;
  /** Where Back from the list goes — the desk — and from the order it opened on. */
  onExit?: (() => void) | undefined;
}) {
  const [screen, setScreen] = useState<Screen>(() =>
    startId === undefined ? { kind: 'list' } : { kind: 'view', id: startId },
  );
  const toList = () => setScreen({ kind: 'list' });
  const toOrder = (id: number) => setScreen({ kind: 'view', id });
  // Back from an order goes where it was opened from: the desk for the one it opened on, else the list.
  const backFrom = (id: number) => (onExit && id === startId ? onExit() : toList());
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const branchId = session.branch?.id;
  const openId = screen.kind === 'list' || screen.kind === 'new' ? null : screen.id;
  const order = usePurchaseOrder(openId);
  const letterhead = useLetterhead(branchId);

  switch (screen.kind) {
    case 'new':
      return (
        <OrderComposer
          session={session}
          vendorId={null}
          trail="Purchase orders"
          onClose={toList}
          onSaved={(saved, print) =>
            setScreen(print ? { kind: 'print', id: saved.id } : { kind: 'view', id: saved.id })
          }
        />
      );
    case 'receive':
      return (
        <ReceiveScreen
          session={session}
          orderId={screen.id}
          onClose={() => toOrder(screen.id)}
          onDone={() => toOrder(screen.id)}
        />
      );
    case 'bill':
      return (
        <BillScreen
          vendorId={screen.vendorId}
          trail={order.data?.documentNumber}
          onClose={() => toOrder(screen.id)}
          onSaved={(_, payNow) =>
            setScreen(payNow ? { ...screen, kind: 'pay' } : { kind: 'view', id: screen.id })
          }
        />
      );
    case 'pay':
      return (
        <PayScreen
          session={session}
          vendorId={screen.vendorId}
          onClose={() => toOrder(screen.id)}
          onDone={() => toOrder(screen.id)}
          onSplit={() => setScreen({ ...screen, kind: 'split' })}
        />
      );
    case 'split':
      return (
        <VendorPaymentsPage
          session={session}
          payVendorId={screen.vendorId}
          onExit={() => toOrder(screen.id)}
        />
      );
    case 'openBill':
      return (
        <PurchaseBillsPage
          session={session}
          openId={screen.billId}
          onExit={() => toOrder(screen.id)}
        />
      );
    case 'list':
      return (
        <OrderList
          session={session}
          today={today}
          onBack={onExit}
          onNew={() => setScreen({ kind: 'new' })}
          onOpen={toOrder}
        />
      );
  }

  if (!order.data) {
    return null;
  }

  const current = order.data;

  if (screen.kind === 'edit') {
    return (
      <OrderComposer
        session={session}
        vendorId={current.supplierVendorId}
        editing={current}
        trail={current.documentNumber}
        onClose={() => toOrder(current.id)}
        onSaved={(saved, print) =>
          setScreen(print ? { kind: 'print', id: saved.id } : { kind: 'view', id: saved.id })
        }
      />
    );
  }

  if (screen.kind === 'print') {
    return (
      <PrintFrame
        onClose={() => toOrder(current.id)}
        title={`Purchase order ${current.documentNumber}`}
        sizes={['A4', 'A5']}
        remember="purchase-order"
      >
        {(paper) => (
          <PurchaseOrderSheet paper={paper} order={current} letterhead={letterhead.data} />
        )}
      </PrintFrame>
    );
  }

  return (
    <OrderView
      session={session}
      order={current}
      today={today}
      letterhead={letterhead.data}
      onBack={() => backFrom(current.id)}
      onEdit={() => setScreen({ kind: 'edit', id: current.id })}
      onPrint={() => setScreen({ kind: 'print', id: current.id })}
      onOpenBill={(billId) => setScreen({ kind: 'openBill', id: current.id, billId })}
      onMove={(move) =>
        setScreen(
          move === 'receive'
            ? { kind: 'receive', id: current.id }
            : { kind: move, id: current.id, vendorId: current.supplierVendorId },
        )
      }
    />
  );
}

/* ------------------------------------------------------------------------------------------------ */

/** The status chips, in the order an order moves through them; null is every order. */
const statusChips: { value: PurchaseOrderStatus | null; label: string }[] = [
  { value: null, label: 'All' },
  ...PurchaseOrderStatusValues.map((value) => ({ value, label: orderStatusLabel[value] })),
];

/** The received bar takes the status's colour: brand while open, amber part-way, green when in. */
const barTone: Record<PurchaseOrderStatus, string> = {
  Open: 'bg-primary',
  PartlyReceived: 'bg-[color:var(--color-warning)]',
  Received: 'bg-success',
  Cancelled: 'bg-gray-300',
  ClosedShort: 'bg-gray-300',
};

function OrderList({
  session,
  today,
  onBack,
  onNew,
  onOpen,
}: {
  session: Session;
  today: string;
  /** Back to the supplier desk, when the list was opened from it. */
  onBack?: (() => void) | undefined;
  onNew: () => void;
  onOpen: (id: number) => void;
}) {
  const branchId = session.branch?.id;
  const [status, setStatus] = useState<PurchaseOrderStatus | null>(null);
  const [supplierVendorId, setSupplierVendorId] = useState<number | undefined>(undefined);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search);
  const orders = usePurchaseOrders(branchId, {
    status: status ?? undefined,
    supplierVendorId,
    from: from || undefined,
    to: to || undefined,
    search: debounced || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const glance = usePurchaseOrderGlance(branchId);
  const suppliers = useVendors(branchId, { capability: 'Supplier', pageSize: 200 });
  const maySeeValue = can(session, 'PurchaseOrder:ViewFinancials');
  const total = orders.data?.total ?? 0;
  const filtered =
    status !== null || supplierVendorId !== undefined || from !== '' || to !== '' || search !== '';
  // Every filter starts the list again from its first page.
  const refilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: DataViewColumn<PurchaseOrderSummary>[] = [
    {
      title: 'PO number',
      dataIndex: 'documentNumber',
      primary: true,
      render: (_, row) => (
        <div>
          <button
            type="button"
            onClick={() => onOpen(row.id)}
            className="text-primary font-mono-nums cursor-pointer text-[length:var(--scale-list-name)] font-semibold hover:underline"
          >
            {row.documentNumber}
          </button>
          <div className="text-default text-[length:var(--scale-list-meta)]">{orderSize(row)}</div>
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      render: (_, row) => (
        <div>
          <div className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {row.supplierName}
          </div>
          {row.supplierMobile && (
            <div className="text-default font-mono-nums text-[length:var(--scale-list-meta)]">
              {row.supplierMobile}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Ordered on',
      dataIndex: 'orderDate',
      hideOnCard: true,
      render: (_, row) => (
        <span className="text-[length:var(--scale-list-name)] whitespace-nowrap text-gray-900">
          {formatDocumentDate(row.orderDate)}
        </span>
      ),
    },
    {
      title: 'Expected',
      dataIndex: 'expectedOn',
      render: (_, row) => {
        const late = lateWords(row, today);

        return (
          <div className="whitespace-nowrap">
            <div className="text-[length:var(--scale-list-name)] text-gray-900">
              {row.expectedOn ? formatDocumentDate(row.expectedOn) : '—'}
            </div>
            {late && (
              <div className="text-[length:var(--scale-list-meta)] font-semibold text-[color:var(--color-warning)]">
                {late}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Received',
      dataIndex: 'receivedQuantity',
      render: (_, row) => (
        <div className="min-w-32">
          <div className="font-mono-nums text-[length:var(--scale-list-name)] text-gray-900">
            {row.receivedQuantity} of {row.orderedQuantity}
          </div>
          <div className="bg-light mt-1.5 h-1.5 overflow-hidden rounded-full" aria-hidden="true">
            <div
              className={`h-1.5 rounded-full ${barTone[row.status]}`}
              style={{ width: `${receivedShare(row)}%` }}
            />
          </div>
        </div>
      ),
    },
    ...(maySeeValue
      ? [
          {
            title: 'Value',
            dataIndex: 'orderValue',
            align: 'right' as const,
            hideOnCard: true,
            render: (_: unknown, row: PurchaseOrderSummary) => (
              <div className="text-end whitespace-nowrap">
                <div className="font-mono-nums text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                  {row.orderValue === null ? '—' : rupees(row.orderValue)}
                </div>
                <div className="text-default text-[length:var(--scale-list-meta)]">
                  {row.orderValue === null ? 'no rate agreed' : 'before tax'}
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, row) => (
        <StatusBadge tone={orderStatusTone[row.status]}>{orderStatusLabel[row.status]}</StatusBadge>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      trailing: true,
      render: (_, row) => (
        <IconAction
          icon="icon icon-chevron-right"
          label={`Open ${row.documentNumber}`}
          onClick={() => onOpen(row.id)}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeading
        eyebrow="Shop · Purchases"
        title="Purchase orders"
        subtitle="What the shop has asked its suppliers for, and how much of it has come."
        actions={
          <>
            {onBack && (
              <Button icon="icon icon-arrow-left" shortcut="back" onClick={onBack}>
                Suppliers
              </Button>
            )}
            {canAct(session, 'PurchaseOrder:Create') && (
              <Button variant="primary" icon="icon icon-plus" shortcut="create" onClick={onNew}>
                New purchase order
              </Button>
            )}
          </>
        }
      />

      {glance.data && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Figure
            label="Open orders"
            value={String(glance.data.openOrders)}
            hint="still expecting stock"
            icon="icon icon-file-text"
            hue="primary"
          />
          <Figure
            label="Still to come"
            value={String(glance.data.stillToCome)}
            hint="garments across open orders"
            icon="icon icon-package"
            hue="purple"
          />
          <Figure
            label="Late"
            value={String(glance.data.late)}
            hint="past its expected day"
            icon="icon icon-clock"
            hue="indigo"
          />
          {maySeeValue && (
            <Figure
              label="On order"
              value={glance.data.valueToCome === null ? '—' : rupees(glance.data.valueToCome)}
              hint="still to come, before tax"
              icon="icon icon-indian-rupee"
              hue="teal"
            />
          )}
        </div>
      )}

      <Card flush>
        <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative flex items-center">
              <i
                className="icon icon-search text-default pointer-events-none absolute start-3"
                aria-hidden="true"
              />
              <input
                type="search"
                aria-label="Search orders"
                placeholder="PO number, supplier or design"
                value={search}
                onChange={(event) => refilter(setSearch)(event.target.value)}
                className="border-border-color h-9 w-64 max-w-full rounded-md border bg-white ps-9 pe-3 text-[length:var(--scale-list-name)] text-gray-900"
              />
            </label>
            <DatePicker
              className="w-36"
              placeholder="From"
              aria-label="Ordered from"
              value={toDayjs(from) ?? null}
              onChange={(value) => refilter(setFrom)(toIsoDate(value) ?? '')}
            />
            <DatePicker
              className="w-36"
              placeholder="To"
              aria-label="Ordered up to"
              value={toDayjs(to) ?? null}
              onChange={(value) => refilter(setTo)(toIsoDate(value) ?? '')}
            />
            <select
              aria-label="Supplier"
              value={supplierVendorId ?? ''}
              onChange={(event) =>
                refilter(setSupplierVendorId)(
                  event.target.value === '' ? undefined : Number(event.target.value),
                )
              }
              className={`${selectClass} h-9 w-48`}
            >
              <option value="">All suppliers</option>
              {(suppliers.data?.items ?? []).map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>
          <ul aria-label="Filter by status" className="m-0 flex list-none flex-wrap gap-1.5 ps-0">
            {statusChips.map((chip) => (
              <li key={chip.label} className="list-none">
                <button
                  type="button"
                  aria-pressed={status === chip.value}
                  onClick={() => refilter(setStatus)(chip.value)}
                  className={`cursor-pointer rounded-full border px-3 py-1 text-[12px] font-medium ${
                    status === chip.value
                      ? 'border-primary bg-primary text-white'
                      : 'border-border-color text-default bg-white hover:text-gray-900'
                  }`}
                >
                  {chip.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <DataView<PurchaseOrderSummary>
          columns={columns}
          dataSource={orders.data?.items ?? []}
          loading={orders.isLoading}
          empty={
            <EmptyState
              icon="icon icon-shopping-cart"
              title={filtered ? 'No order matches' : 'No purchase order yet'}
              hint={
                filtered
                  ? 'Clear a filter, or pick another status.'
                  : 'Raise one with New purchase order; receive the stock against it as it arrives.'
              }
            />
          }
        />

        {total > 0 && (
          <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
            <span className="text-default text-[length:var(--scale-list-meta)]">
              {total} order{total === 1 ? '' : 's'}
            </span>
            {total > PAGE_SIZE && (
              <Pagination
                current={page}
                total={total}
                pageSize={PAGE_SIZE}
                onChange={setPage}
                showSizeChanger={false}
              />
            )}
          </div>
        )}
      </Card>
    </>
  );
}

/** A figure in the heading row: muted label, the figure, a hint, the hue's tile on the right. */
function Figure({
  label,
  value,
  hint,
  icon,
  hue,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
  hue: Hue;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>
          <p className="font-mono-nums mb-0 text-2xl font-bold text-gray-900 max-lg:text-xl">
            {value}
          </p>
          <p className="text-default mt-0.5 mb-0 truncate text-[length:var(--scale-list-meta)]">
            {hint}
          </p>
        </div>
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses[hue].tile}`}
          aria-hidden="true"
        >
          <i className={`${icon} text-lg text-white`} />
        </span>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------------------------------ */

/** Each step's ground and mark: green when done, amber while it is where the order stands. */
const stepLook: Record<OrderStep['state'], { ground: string; dot: string }> = {
  done: { ground: 'bg-success-transparent', dot: 'bg-success text-white' },
  now: { ground: 'bg-warning-transparent', dot: 'bg-[color:var(--color-warning)] text-white' },
  later: { ground: 'bg-light', dot: 'bg-border-color text-gray-900' },
};

function OrderView({
  session,
  order,
  today,
  letterhead,
  onBack,
  onEdit,
  onPrint,
  onOpenBill,
  onMove,
}: {
  session: Session;
  order: PurchaseOrder;
  today: string;
  /** The shop as it prints — the issuer at the top of the order. */
  letterhead: PrintLetterhead | undefined;
  onBack: () => void;
  onEdit: () => void;
  onPrint: () => void;
  onOpenBill: (billId: number) => void;
  onMove: (move: StepMove) => void;
}) {
  const mayEdit = canAct(session, 'PurchaseOrder:Edit');
  const maySeeValue = can(session, 'PurchaseOrder:ViewFinancials');
  const maySeeBills = can(session, 'PurchaseBill:View');
  const steps = orderSteps(order, maySeeBills);
  const late = lateWords(order, today);
  const unbilled = Math.max(0, order.receivedQuantity - order.billedQuantity);

  // A move is offered only to someone who may make it.
  const mayMove: Record<StepMove, boolean> = {
    receive: canAct(session, 'ItemUnit:Create') && canReceive(order),
    bill: canAct(session, 'PurchaseBill:Create'),
    pay: canAct(session, 'VendorPayment:Create'),
  };
  const moveLabel = moveWords(order);

  const end = useApiMutation<PurchaseOrder, { how: 'cancel' | 'close'; reason: string }>({
    mutationFn: ({ how, reason }) =>
      how === 'cancel'
        ? cancelPurchaseOrder(order.id, reason)
        : closePurchaseOrder(order.id, reason),
    success: (saved) => `${saved.documentNumber} ${orderStatusLabel[saved.status].toLowerCase()}.`,
    invalidates: [purchaseOrdersKey],
  });

  const columns: DocumentColumn<PurchaseOrderLine>[] = [
    {
      key: 'design',
      title: 'Design',
      render: (line) => <LineName name={line.itemName} note={line.itemCode} />,
    },
    { key: 'look', title: 'Colour · size', render: (line) => line.variantDisplayName },
    { key: 'ordered', title: 'Ordered', numeric: true, render: (line) => line.quantity },
    {
      key: 'came',
      title: 'Came',
      numeric: true,
      render: (line) => (
        <span className="font-semibold text-[color:var(--color-success)]">
          {line.receivedQuantity}
        </span>
      ),
    },
    {
      key: 'pending',
      title: 'To come',
      numeric: true,
      render: (line) =>
        line.pendingQuantity === 0 ? (
          <span className="text-default">—</span>
        ) : (
          <span className="font-semibold text-[color:var(--color-warning)]">
            {line.pendingQuantity}
          </span>
        ),
    },
    ...(maySeeValue
      ? ([
          {
            key: 'rate',
            title: 'Rate',
            numeric: true,
            render: (line) => (line.rate === null ? '—' : rupees(line.rate)),
          },
          {
            key: 'amount',
            title: 'Amount',
            numeric: true,
            render: (line) => (
              <span className="font-semibold">
                {line.rate === null ? '—' : rupees(line.rate * line.quantity)}
              </span>
            ),
          },
        ] satisfies DocumentColumn<PurchaseOrderLine>[])
      : []),
  ];

  const pending = order.lines.reduce((sum, line) => sum + line.pendingQuantity, 0);

  return (
    <>
      <DocumentHeading
        trail="Purchase orders"
        current={order.supplierName}
        title={order.documentNumber}
        status={{ label: orderStatusLabel[order.status], tone: orderStatusTone[order.status] }}
        subtitle={[
          `Ordered ${formatDocumentDate(order.orderDate)}`,
          order.expectedOn
            ? `expected ${formatDocumentDate(order.expectedOn)}${late ? `, ${late}` : ''}`
            : null,
          `${order.receivedQuantity} of ${order.orderedQuantity} garments have come`,
        ]
          .filter(Boolean)
          .join(' · ')}
        onBack={onBack}
        actions={
          <>
            {can(session, 'PurchaseOrder:Print') && (
              <Button icon="icon icon-printer" shortcut="print" onClick={onPrint}>
                Print
              </Button>
            )}
            {mayEdit && canCancel(order) && (
              <ReasonAction
                label="Cancel order"
                title="Cancel this order?"
                hint="Nothing has come against it. The number stays in the sequence."
                busy={end.isPending}
                onConfirm={(reason) => end.mutate({ how: 'cancel', reason })}
              />
            )}
            {mayEdit && canCloseShort(order) && (
              <ReasonAction
                label="Close short"
                title="Close the rest of this order?"
                hint="What has come stays received; the rest is no longer expected."
                busy={end.isPending}
                onConfirm={(reason) => end.mutate({ how: 'close', reason })}
              />
            )}
            {mayEdit && order.canEdit && (
              <Button icon="icon icon-pencil" onClick={onEdit}>
                Change
              </Button>
            )}
            {mayMove.receive && (
              <Button
                variant="primary"
                icon="icon icon-package-check"
                onClick={() => onMove('receive')}
              >
                {moveLabel.receive}
              </Button>
            )}
          </>
        }
      />

      {order.endReason && (
        <div className="mb-4">
          <Notice
            tone="info"
            title={`${orderStatusLabel[order.status]}${order.endedOn ? ` on ${formatDocumentDate(order.endedOn)}` : ''}`}
          >
            {order.endReason}
          </Notice>
        </div>
      )}

      {steps.length > 0 && (
        <section aria-label="Where this order is" className="mb-4">
          <Card>
            <ol className="m-0 grid list-none grid-cols-1 gap-3 ps-0 sm:grid-cols-2 xl:grid-cols-4">
              {steps.map((step, index) => (
                <li
                  key={step.key}
                  className={`flex list-none items-start gap-3 rounded-xl p-3.5 ${stepLook[step.state].ground}`}
                >
                  <span
                    className={`flex size-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${stepLook[step.state].dot}`}
                    aria-hidden="true"
                  >
                    {step.state === 'done' ? <i className="icon icon-check" /> : index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="mb-0 text-[length:var(--scale-list-name)] font-bold text-gray-900">
                      {step.title}
                      <span className="sr-only">
                        {' '}
                        — {step.state === 'done' ? 'done' : 'not done'}
                      </span>
                    </p>
                    <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                      {step.line}
                    </p>
                    {step.move && mayMove[step.move] && (
                      <button
                        type="button"
                        onClick={() => onMove(step.move!)}
                        className="text-primary mt-1.5 cursor-pointer text-[length:var(--scale-list-meta)] font-semibold hover:underline"
                      >
                        {moveLabel[step.move]} →
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </section>
      )}

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <DocumentPaper>
            <DocumentTop
              issuer={letterhead?.tenantName ?? order.branchName}
              issuerLines={[
                letterhead ? letterhead.branchName : null,
                ...(letterhead?.addressLines ?? []),
                letterhead?.gstin ? `GSTIN ${letterhead.gstin}` : null,
              ]}
              title="Purchase order"
              number={order.documentNumber}
              status={{
                label: orderStatusLabel[order.status],
                tone: orderStatusTone[order.status],
              }}
            />
            <DocumentParties
              heading="Supplier"
              name={order.supplierName}
              lines={[
                [order.supplierPlace, order.supplierMobile].filter(Boolean).join(' · ') || null,
                order.supplierGstin ? `GSTIN ${order.supplierGstin}` : null,
              ]}
              facts={[
                { label: 'Ordered on', value: formatDocumentDate(order.orderDate) },
                ...(order.expectedOn
                  ? [{ label: 'Expected', value: formatDocumentDate(order.expectedOn) }]
                  : []),
                { label: 'Deliver to', value: order.branchName },
                ...(maySeeValue ? [{ label: 'Rates', value: 'before tax' }] : []),
              ]}
            />
            <DocumentLines columns={columns} rows={order.lines} rowKey={(line) => line.id} />
            <div className="flex flex-wrap justify-between gap-5">
              <div className="min-w-0 flex-1 basis-64 text-[length:var(--scale-list-meta)]">
                {order.notes && (
                  <>
                    <p className="mb-1 font-semibold text-gray-900">Notes</p>
                    <p className="text-default mb-0">{order.notes}</p>
                  </>
                )}
              </div>
              <div className="w-full sm:w-72">
                <DocumentTotals
                  rows={[
                    { label: 'Garments', value: String(order.orderedQuantity) },
                    { label: 'Still to come', value: String(pending) },
                    ...(maySeeValue
                      ? [
                          {
                            label: 'Value before tax',
                            value: order.orderValue === null ? '—' : rupees(order.orderValue),
                            grand: true,
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </div>
          </DocumentPaper>
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle icon="icon icon-package-check" hue="teal">
              Deliveries
            </SectionTitle>
            {order.receipts.length === 0 ? (
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Nothing has come against this order yet.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col ps-0">
                {order.receipts.map((receipt) => (
                  <li
                    key={receipt.id}
                    className="border-border-color flex list-none items-center justify-between gap-3 border-b py-2.5 last:border-b-0"
                  >
                    <div>
                      <p className="font-mono-nums mb-0 text-xs font-semibold text-gray-900">
                        {receipt.documentNumber}
                      </p>
                      <p className="text-default mb-0 text-[11px]">
                        {formatDocumentDate(receipt.receivedOn)}
                      </p>
                    </div>
                    <span className="text-end text-[13px] text-gray-900">
                      {receipt.units} garment{receipt.units === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {maySeeBills && (
            <Card>
              <SectionTitle icon="icon icon-file-text" hue="info">
                Their bills
              </SectionTitle>
              {order.bills.length === 0 ? (
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  No bill carries this order yet.
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col ps-0">
                  {order.bills.map((bill) => (
                    <li
                      key={bill.id}
                      className="border-border-color flex list-none items-center justify-between gap-3 border-b py-2.5 last:border-b-0"
                    >
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => onOpenBill(bill.id)}
                          className="text-primary font-mono-nums cursor-pointer text-xs font-semibold hover:underline"
                        >
                          {bill.documentNumber}
                        </button>
                        <p className="text-default mb-0 text-[11px]">
                          Their no. {bill.supplierBillNumber} · {bill.garments} garment
                          {bill.garments === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="text-end">
                        <p className="font-mono-nums mb-0.5 text-[13px] font-semibold text-gray-900">
                          {rupees(bill.billTotal)}
                        </p>
                        <StatusBadge tone={billStatusTone[bill.status]}>
                          {billStatusLabel[bill.status]}
                        </StatusBadge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {unbilled > 0 && (
                <>
                  <p className="text-default mt-3 mb-0 text-[length:var(--scale-list-meta)]">
                    {unbilled} garment{unbilled === 1 ? '' : 's'} came and{' '}
                    {unbilled === 1 ? 'is' : 'are'} not on a bill yet.
                  </p>
                  {mayMove.bill && (
                    <button
                      type="button"
                      onClick={() => onMove('bill')}
                      className="text-primary mt-1.5 cursor-pointer text-[length:var(--scale-list-meta)] font-semibold hover:underline"
                    >
                      Enter their bill →
                    </button>
                  )}
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/** A move that needs a reason — the server's rule, asked for before it fires, as voiding an expense is. */
function ReasonAction({
  label,
  title,
  hint,
  busy,
  onConfirm,
}: {
  label: string;
  title: string;
  hint: string;
  busy: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Popconfirm
      title={title}
      description={
        <div className="w-60 pt-1">
          <p className="text-default mb-1.5 text-[12px]">{hint}</p>
          <input
            type="text"
            placeholder="Why?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
          />
        </div>
      }
      okText={label}
      okButtonProps={{ danger: true, loading: busy, disabled: reason.trim().length === 0 }}
      cancelText="Keep it"
      onConfirm={() => {
        onConfirm(reason.trim());
        setReason('');
      }}
      placement="bottomRight"
    >
      <Button disabled={busy}>{label}</Button>
    </Popconfirm>
  );
}
